import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../../../../shared/ui/Modal';
import { GitHubService } from '../../../tabs/code';
import { getRouter } from '../../../../../shared/lib/comm/gateway_ws/connection';
import { useWebSocketStatus } from '../../../../../shared/lib/comm/gateway_ws/useWebSocketStatus';
import type { GitHubPullRequest, GitHubPRStatus, GitHubPRReview } from '../../../tabs/code';

// Type for deployment payload
type DeployPayload = {
    contract: {
        settings: {
            aws: {
                crossAccountRoleArn: string;
                accountId: string;
                region: string;
            };
        };
    };
};

interface BuildSummaryData {
    lowLevelRequirementId: string;
    status: string;
    branchLink: string;
    prLink: string;
    documentLinks: string[];
    keyMetrics: string;
    dashboardLinks: string[];
    alerts: string;
    logs: string;
    productManager: string;
    devManager: string;
}

interface BuildSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    buildSummaryData: BuildSummaryData | null;
    setBuildSummaryData: React.Dispatch<React.SetStateAction<BuildSummaryData | null>>;
    editingField: string | null;
    editValue: string;
    startEditing: (field: string, value: string) => void;
    saveEdit: () => void;
    cancelEdit: () => void;
    // Add new prop for VishCoder PR response
    vishCoderPRResponse?: {
        pr_url: string;
        pr_number: number;
        status: string;
        agent: string;
        llm: string;
        details: string;
        progress: number;
    } | null;
    // Add WebSocket connection for deployment
    agentSocket?: WebSocket | null;
}

// New interface for PR status information
interface PRStatusInfo {
    isLoading: boolean;
    error: string | null;
    prInfo: {
        pr: GitHubPullRequest;
        status: GitHubPRStatus[];
        reviews: GitHubPRReview[];
        approvalInfo: {
            approved: boolean;
            reviewCount: number;
            approvalCount: number;
            changesRequested: boolean;
            lastReviewState: string;
            prState: string;
            mergeable: boolean;
        };
    } | null;
}

// New interface for deployment status
interface DeploymentStatus {
    isDeploying: boolean;
    status: 'idle' | 'initiating' | 'deploying' | 'success' | 'failed';
    message: string;
    error?: string;
}

const BuildSummaryModal: React.FC<BuildSummaryModalProps> = ({
    isOpen,
    onClose,
    buildSummaryData,
    setBuildSummaryData,
    editingField,
    editValue,
    startEditing,
    saveEdit,
    cancelEdit,
    vishCoderPRResponse,
    agentSocket
}) => {
    // Use WebSocket status hook for health monitoring
    const wsStatus = useWebSocketStatus();
    
    // New state for PR status checking
    const [prStatusInfo, setPrStatusInfo] = useState<PRStatusInfo>({
        isLoading: false,
        error: null,
        prInfo: null
    });
    
    // New state for deployment
    const [deploymentStatus, setDeploymentStatus] = useState<DeploymentStatus>({
        isDeploying: false,
        status: 'idle',
        message: ''
    });

    // Function to check PR status
    const checkPRStatus = async (prUrl: string) => {
        if (!prUrl || !prUrl.includes('github.com')) return;
        
        setPrStatusInfo(prev => ({ ...prev, isLoading: true, error: null }));
        
        try {
            const prInfo = await GitHubService.getPullRequestInfo(prUrl);
            setPrStatusInfo({
                isLoading: false,
                error: null,
                prInfo
            });
            
            // Automatically update build summary status based on GitHub PR state
            if (prInfo && buildSummaryData) {
                let newStatus = buildSummaryData.status;
                
                if (prInfo.pr.state === 'closed') {
                    if (prInfo.pr.merged_at) {
                        newStatus = 'Merged';
                        console.log('🔄 PR status updated to: Merged');
                    } else {
                        newStatus = 'Build Complete'; // PR was closed without merging
                        console.log('🔄 PR status updated to: Build Complete (closed without merge)');
                    }
                } else if (prInfo.pr.state === 'open') {
                    // Check if PR has approvals
                    if (prInfo.approvalInfo.approved) {
                        newStatus = 'PR Approved'; // PR is approved and ready for deployment
                        console.log('🔄 PR status updated to: PR Approved');
                    } else {
                        newStatus = 'PR'; // PR is open, waiting for review
                        console.log('🔄 PR status updated to: PR (waiting for review)');
                    }
                }
                
                // Update status if it changed
                if (newStatus !== buildSummaryData.status) {
                    setBuildSummaryData(prev => prev ? { ...prev, status: newStatus } : null);
                    console.log(`✅ Build summary status updated from "${buildSummaryData.status}" to "${newStatus}"`);
                }
            }
        } catch (error) {
            setPrStatusInfo({
                isLoading: false,
                error: error instanceof Error ? error.message : 'Failed to check PR status',
                prInfo: null
            });
        }
    };

    // Function to handle deployment
    const handleDeploy = async () => {
        if (!wsStatus.isConnected) {
            setDeploymentStatus({
                isDeploying: false,
                status: 'failed',
                message: 'WebSocket connection not available',
                error: 'No active connection to VishCoder'
            });
            return;
        }

        setDeploymentStatus({
            isDeploying: true,
            status: 'initiating',
            message: 'Initiating deployment...'
        });

        try {
            const router = getRouter();
            const comm = router.getCommunicationManager();
            
            if (!comm || !wsStatus.isConnected) {
                setDeploymentStatus({
                    isDeploying: false,
                    status: 'failed',
                    message: 'WebSocket not connected',
                    error: 'Cannot send deployment request: WebSocket connection not available'
                });
                return;
            }

            // Set up channel context for deployment
            const threadId = buildSummaryData?.lowLevelRequirementId || `deploy_${Date.now()}`;
            const messageId = crypto.randomUUID();
            const timestamp = new Date().toISOString();
            
            router.setChannelContext('deploy', {
                threadId,
                originMessageId: messageId,
                originActor: 'system',
                origints: timestamp
            }, messageId, 'system', timestamp);

            // Create deployment payload with AWS settings
            // Note: These values should come from user configuration or settings
            // In production, these would be loaded from:
            // 1. User's AWS account settings in the app
            // 2. Cross-account role ARN configured by the user
            // 3. Target AWS region for deployment
            const deployPayload: DeployPayload = {
                contract: {
                    settings: {
                        aws: {
                            crossAccountRoleArn: 'arn:aws:iam::123456789012:role/VishCoder-TerraformExecutionRole',
                            accountId: '123456789012',
                            region: 'us-east-1'
                        }
                    }
                }
            };

            // Send deployment request via WebSocket using router
            comm.emit('deploy', 'system', 'streaming', 'status', deployPayload, 'request');

            setDeploymentStatus({
                isDeploying: true,
                status: 'deploying',
                message: 'Deployment request sent to VishCoder. Waiting for response...'
            });

            console.log('🚀 Deployment request sent to VishCoder:', deployPayload);

        } catch (error) {
            console.error('Deployment failed:', error);
            setDeploymentStatus({
                isDeploying: false,
                status: 'failed',
                message: 'Failed to initiate deployment',
                error: error instanceof Error ? error.message : 'Unknown error'
            });
        }
    };

    // Function to handle deployment response from VishCoder
    const handleDeploymentResponse = useCallback((response: any) => {
        //console.log('📥 Received deployment response:', response);
        
        if (response.type === 'deploy_feature') {
            if (response.status === 'Success' || response.status === 'Completed') {
                // Deployment successful
                setDeploymentStatus({
                    isDeploying: false,
                    status: 'success',
                    message: 'Deployment completed successfully!'
                });
                
                // Update build summary status
                if (buildSummaryData) {
                    setBuildSummaryData(prev => prev ? { ...prev, status: 'Deployed Dev' } : null);
                }
                
                console.log('✅ Deployment completed successfully');
            } else if (response.status === 'Failed' || response.status === 'Error') {
                // Deployment failed
                setDeploymentStatus({
                    isDeploying: false,
                    status: 'failed',
                    message: 'Deployment failed',
                    error: response.body?.statusDetails?.details || 'Unknown deployment error'
                });
                
                console.error('❌ Deployment failed:', response.body?.statusDetails?.details);
            } else if (response.status === 'InProgress') {
                // Deployment in progress
                setDeploymentStatus({
                    isDeploying: true,
                    status: 'deploying',
                    message: response.body?.statusDetails?.details || 'Deployment in progress...'
                });
            }
        }
    }, [buildSummaryData, setBuildSummaryData]);

    // Listen for deployment responses from VishCoder
    useEffect(() => {
        if (!agentSocket) return;

        const handleMessage = (event: MessageEvent) => {
            try {
                const response = JSON.parse(event.data);
                handleDeploymentResponse(response);
            } catch (error) {
                console.error('Failed to parse deployment response:', error);
            }
        };

        agentSocket.addEventListener('message', handleMessage);
        return () => agentSocket.removeEventListener('message', handleMessage);
    }, [agentSocket, handleDeploymentResponse]);

    // Check PR status when PR link changes
    useEffect(() => {
        if (buildSummaryData?.prLink) {
            checkPRStatus(buildSummaryData.prLink);
        }
    }, [buildSummaryData?.prLink]);

    // Function to open external links
    const openExternalLink = (url: string) => {
        if (url && url.startsWith('http')) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    // Function to handle VishCoder PR response
    const handleVishCoderPRResponse = useCallback((response: NonNullable<typeof vishCoderPRResponse>) => {
        if (!response?.pr_url) return;
        
        console.log('🚀 Processing VishCoder PR response:', response);
        
        // Update build summary data with PR information
        if (buildSummaryData) {
            setBuildSummaryData(prev => prev ? {
                ...prev,
                prLink: response.pr_url,
                status: 'PR' // Set to 'PR' - the actual status will be determined by GitHub PR checking
            } : null);
        } else {
            // Create new build summary data if it doesn't exist
            const newBuildSummaryData = {
                lowLevelRequirementId: `req_${Date.now()}_${Math.floor(Math.random() * 1000000)}`,
                status: 'PR', // Initial status is 'PR' - will be updated based on actual GitHub PR state
                branchLink: '', // Will be populated when branch info is available
                prLink: response.pr_url,
                documentLinks: [],
                keyMetrics: 'Response time < 200ms, 99.9% uptime, Error rate < 0.1%',
                dashboardLinks: [],
                alerts: '',
                logs: '',
                productManager: '',
                devManager: ''
            };
            setBuildSummaryData(newBuildSummaryData);
        }
        
        // Automatically check PR status to get the real GitHub PR state
        checkPRStatus(response.pr_url);
        
        console.log('✅ PR information updated from VishCoder response - status set to "PR" for manual review');
    }, [buildSummaryData, setBuildSummaryData, checkPRStatus]);

    // Effect to handle VishCoder PR response when it changes
    useEffect(() => {
        if (vishCoderPRResponse) {
            handleVishCoderPRResponse(vishCoderPRResponse);
        }
    }, [vishCoderPRResponse, handleVishCoderPRResponse]);

    if (!buildSummaryData) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Build Summary"
            size="xl"
        >
            <div className="space-y-6">
                {/* VishCoder Response Notification */}
                {vishCoderPRResponse && (
                    <div className="bg-gradient-to-r from-purple-500/20 to-green-500/20 border border-purple-400/30 rounded-xl p-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                <h4 className="text-purple-400 font-medium">VishCoder Response Received</h4>
                                <p className="text-purple-300/80 text-sm mb-2">
                                    Pull Request #{vishCoderPRResponse.pr_number} has been created successfully by {vishCoderPRResponse.agent} using {vishCoderPRResponse.llm}.
                                </p>
                                <div className="flex items-center space-x-4 text-xs">
                                    <span className="text-purple-300/80">
                                        VishCoder Status: <span className="text-green-400">{vishCoderPRResponse.status}</span>
                                    </span>
                                    <span className="text-purple-300/80">
                                        Progress: <span className="text-blue-400">{vishCoderPRResponse.progress}%</span>
                                    </span>
                                    <span className="text-purple-300/80">
                                        Details: <span className="text-yellow-400">{vishCoderPRResponse.details}</span>
                                    </span>
                                </div>
                                <p className="text-purple-300/60 text-xs mt-2 italic">
                                    Note: The actual PR status will be updated based on GitHub PR state (open, approved, merged, etc.)
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    if (vishCoderPRResponse.pr_url) {
                                        openExternalLink(vishCoderPRResponse.pr_url);
                                    }
                                }}
                                className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 rounded-lg text-purple-300 hover:text-purple-200 transition-all duration-200 text-sm"
                            >
                                View PR
                            </button>
                        </div>
                    </div>
                )}

                {/* PR Auto-population Notification */}
                {buildSummaryData?.prLink && buildSummaryData.prLink.includes('github.com') && (
                    <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-400/30 rounded-xl p-4">
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h4 className="text-green-400 font-medium">PR Information Auto-populated</h4>
                                <p className="text-green-300/80 text-sm">
                                    Pull Request information has been automatically populated from VishCoder. 
                                    The PR link is now clickable and status checking is enabled.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-white/90 mb-2">
                            Low Level Requirement ID
                        </label>
                        <div className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white font-mono text-sm">
                            {buildSummaryData.lowLevelRequirementId}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-white/90 mb-2">
                            Status
                        </label>
                        <div className="relative">
                            <div className="flex items-center space-x-2">
                                <select
                                    value={buildSummaryData.status}
                                    onChange={(e) => setBuildSummaryData(prev => prev ? {...prev, status: e.target.value} : null)}
                                    className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 transition-all duration-300 backdrop-blur-sm"
                                >
                                    <option value="Build In Progress" className="bg-gray-800">Build In Progress</option>
                                    <option value="Build Complete" className="bg-gray-800">Build Complete</option>
                                    <option value="PR" className="bg-gray-800">PR</option>
                                    <option value="PR Approved" className="bg-gray-800">PR Approved</option>
                                    <option value="Merged" className="bg-gray-800">Merged</option>
                                    <option value="Deploy Changes" className="bg-gray-800">Deploy Changes</option>
                                    <option value="Deployed Dev" className="bg-gray-800">Deployed Dev</option>
                                    <option value="Deployed Test" className="bg-gray-800">Deployed Test</option>
                                    <option value="Deployed Prod" className="bg-gray-800">Deployed Prod</option>
                                </select>
                                {buildSummaryData?.prLink && (
                                    <button
                                        onClick={() => checkPRStatus(buildSummaryData.prLink)}
                                        disabled={prStatusInfo.isLoading}
                                        className="px-3 py-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 rounded-xl text-blue-300 hover:text-blue-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Refresh PR status from GitHub"
                                    >
                                        {prStatusInfo.isLoading ? (
                                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                        )}
                                    </button>
                                )}
                            </div>
                            {buildSummaryData?.prLink && (
                                <div className="mt-2 text-xs text-white/60">
                                    {prStatusInfo.isLoading ? (
                                        <span className="text-blue-400">🔄 Checking PR status...</span>
                                    ) : prStatusInfo.error ? (
                                        <span className="text-red-400">❌ {prStatusInfo.error}</span>
                                    ) : prStatusInfo.prInfo ? (
                                        <span className="text-green-400">
                                            ✅ PR Status: {prStatusInfo.prInfo.pr.state} 
                                            {prStatusInfo.prInfo.pr.merged_at && ' (merged)'}
                                            {prStatusInfo.prInfo.approvalInfo.approved && ' ✓ Approved'}
                                        </span>
                                    ) : (
                                        <span className="text-yellow-400">⚠️ Click refresh to check PR status</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Build and Deploy */}
                <div className="border-t border-white/10 pt-4">
                    <h3 className="text-lg font-medium text-white flex items-center mb-3">
                        <svg className="w-5 h-5 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                        Build and Deploy
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-white/90 mb-2">
                                Branch Link
                            </label>
                            <div className="relative group">
                                {editingField === 'branchLink' ? (
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setBuildSummaryData(prev => prev ? {...prev, branchLink: e.target.value} : null)}
                                            className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 transition-all duration-300 backdrop-blur-sm"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveEdit();
                                                if (e.key === 'Escape') cancelEdit();
                                            }}
                                            autoFocus
                                        />
                                        <button
                                            onClick={saveEdit}
                                            className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded transition-colors"
                                            title="Save"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                                            title="Cancel"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white break-all text-sm flex items-center justify-between group cursor-pointer hover:bg-white/15 transition-all duration-200"
                                             onClick={() => openExternalLink(buildSummaryData.branchLink)}>
                                            <span className="text-blue-400 hover:text-blue-300 transition-colors">
                                                {buildSummaryData.branchLink}
                                            </span>
                                            <svg className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        </div>
                                        <button
                                            onClick={() => startEditing('branchLink', buildSummaryData.branchLink)}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-white/40 hover:text-white/90 hover:bg-white/20 rounded transition-all duration-200 opacity-0 group-hover:opacity-100"
                                            title="Edit branch link"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-white/90 mb-2">
                                PR Link
                            </label>
                            <div className="relative group">
                                {editingField === 'prLink' ? (
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setBuildSummaryData(prev => prev ? {...prev, prLink: e.target.value} : null)}
                                            className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 transition-all duration-300 backdrop-blur-sm"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveEdit();
                                                if (e.key === 'Escape') cancelEdit();
                                            }}
                                            autoFocus
                                        />
                                        <button
                                            onClick={saveEdit}
                                            className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded transition-colors"
                                            title="Save"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                                            title="Cancel"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white break-all text-sm flex items-center justify-between group cursor-pointer hover:bg-white/15 transition-all duration-200"
                                             onClick={() => openExternalLink(buildSummaryData.prLink)}>
                                            <span className="text-purple-400 hover:text-purple-300 transition-colors">
                                                {buildSummaryData.prLink}
                                            </span>
                                            <svg className="w-4 h-4 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                            </svg>
                                        </div>
                                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                            <button
                                                onClick={() => checkPRStatus(buildSummaryData.prLink)}
                                                className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded transition-colors"
                                                title="Refresh PR status"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => startEditing('prLink', buildSummaryData.prLink)}
                                                className="p-1.5 text-white/40 hover:text-white/90 hover:bg-white/20 rounded transition-colors"
                                                title="Edit PR link"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                            
                            {/* PR Status Display */}
                            {buildSummaryData.prLink && (
                                <div className="mt-3">
                                    {prStatusInfo.isLoading ? (
                                        <div className="flex items-center space-x-2 text-blue-400 text-sm">
                                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            <span>Checking PR status...</span>
                                        </div>
                                    ) : prStatusInfo.error ? (
                                        <div className="flex items-center space-x-2 text-red-400 text-sm">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span>{prStatusInfo.error}</span>
                                        </div>
                                    ) : prStatusInfo.prInfo ? (
                                        <div className="bg-white/5 border border-white/20 rounded-lg p-3 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                        prStatusInfo.prInfo.approvalInfo.prState === 'open' 
                                                            ? 'bg-green-500/20 text-green-400' 
                                                            : prStatusInfo.prInfo.approvalInfo.prState === 'closed'
                                                            ? 'bg-red-500/20 text-red-400'
                                                            : 'bg-purple-500/20 text-purple-400'
                                                    }`}>
                                                        {prStatusInfo.prInfo.approvalInfo.prState.toUpperCase()}
                                                    </span>
                                                    <span className="text-white/70 text-xs">
                                                        #{prStatusInfo.prInfo.pr.number}
                                                    </span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-white/60 text-xs">
                                                        {prStatusInfo.prInfo.approvalInfo.reviewCount} reviews
                                                    </span>
                                                    <span className="text-white/60 text-xs">
                                                        {prStatusInfo.prInfo.approvalInfo.approvalCount} approved
                                                    </span>
                                                </div>
                                            </div>
                                            
                                            <div className="text-white/90 text-sm">
                                                {prStatusInfo.prInfo.pr.title}
                                            </div>
                                            
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                        prStatusInfo.prInfo.approvalInfo.approved
                                                            ? 'bg-green-500/20 text-green-400'
                                                            : prStatusInfo.prInfo.approvalInfo.changesRequested
                                                            ? 'bg-yellow-500/20 text-yellow-400'
                                                            : 'bg-gray-500/20 text-gray-400'
                                                    }`}>
                                                        {prStatusInfo.prInfo.approvalInfo.approved 
                                                            ? 'APPROVED' 
                                                            : prStatusInfo.prInfo.approvalInfo.changesRequested
                                                            ? 'CHANGES REQUESTED'
                                                            : 'PENDING REVIEW'
                                                        }
                                                    </span>
                                                    {prStatusInfo.prInfo.approvalInfo.mergeable && (
                                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                                                            MERGEABLE
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                {/* Deploy Button - Only show when PR is approved */}
                                                {prStatusInfo.prInfo.approvalInfo.approved && (
                                                    <button
                                                        onClick={handleDeploy}
                                                        disabled={deploymentStatus.isDeploying}
                                                        className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-green-600/50 disabled:to-green-700/50 text-white rounded-lg transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:scale-100"
                                                    >
                                                        {deploymentStatus.isDeploying ? (
                                                            <>
                                                                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                </svg>
                                                                <span>Deploying...</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                                                </svg>
                                                                <span>Deploy Changes</span>
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            )}

                            {/* Deployment Status Display */}
                            {deploymentStatus.status !== 'idle' && (
                                <div className="mt-3">
                                    <div className={`bg-white/5 border rounded-lg p-3 ${
                                        deploymentStatus.status === 'success' 
                                            ? 'border-green-400/30' 
                                            : deploymentStatus.status === 'failed'
                                            ? 'border-red-400/30'
                                            : 'border-blue-400/30'
                                    }`}>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-2">
                                                {deploymentStatus.status === 'success' ? (
                                                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center">
                                                        <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                ) : deploymentStatus.status === 'failed' ? (
                                                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center">
                                                        <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </div>
                                                ) : (
                                                    <div className="w-6 h-6 bg-blue-500/20 rounded-full flex items-center justify-center">
                                                        <svg className="w-4 h-4 text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                    </div>
                                                )}
                                                <div>
                                                    <div className={`text-sm font-medium ${
                                                        deploymentStatus.status === 'success' 
                                                            ? 'text-green-400' 
                                                            : deploymentStatus.status === 'failed'
                                                            ? 'text-red-400'
                                                            : 'text-blue-400'
                                                    }`}>
                                                        {deploymentStatus.status === 'success' 
                                                            ? 'Deployment Successful' 
                                                            : deploymentStatus.status === 'failed'
                                                            ? 'Deployment Failed'
                                                            : 'Deployment In Progress'
                                                        }
                                                    </div>
                                                    <div className="text-xs text-white/70">
                                                        {deploymentStatus.message}
                                                    </div>
                                                    {deploymentStatus.error && (
                                                        <div className="text-xs text-red-400 mt-1">
                                                            Error: {deploymentStatus.error}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {deploymentStatus.status === 'success' && (
                                                <div className="text-xs text-green-400">
                                                    Status updated to: Deployed Dev
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Documents */}
                <div className="border-t border-white/10 pt-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-semibold text-white flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            Project Documentation
                        </h3>
                        <button
                            onClick={() => {
                                const newLinks = [...buildSummaryData.documentLinks, ''];
                                setBuildSummaryData(prev => prev ? {...prev, documentLinks: newLinks} : null);
                            }}
                            className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <span>Add Document</span>
                        </button>
                    </div>
                    
                    <div className="space-y-4">
                        {buildSummaryData.documentLinks.map((link, index) => (
                            <div key={index} className="group">
                                {editingField === `doc_${index}` ? (
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-1">
                                            <input
                                                type="text"
                                                value={editValue}
                                                onChange={(e) => {
                                                    const newLinks = [...buildSummaryData.documentLinks];
                                                    newLinks[index] = e.target.value;
                                                    setBuildSummaryData(prev => prev ? {...prev, documentLinks: newLinks} : null);
                                                }}
                                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-400/50 transition-all duration-300 backdrop-blur-sm"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveEdit();
                                                    if (e.key === 'Escape') cancelEdit();
                                                }}
                                                autoFocus
                                                placeholder="Enter document URL..."
                                            />
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={saveEdit}
                                                className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center space-x-1"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span>Save</span>
                                            </button>
                                            <button
                                                onClick={cancelEdit}
                                                className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center space-x-1"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                                <span>Cancel</span>
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-gradient-to-r from-white/5 to-white/10 border border-white/20 rounded-xl p-4 hover:from-white/10 hover:to-white/15 transition-all duration-300 group-hover:shadow-lg">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                                                    <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                    </svg>
                                                </div>
                                                <div className="text-white text-sm font-medium break-all cursor-pointer hover:text-green-400 transition-colors"
                                                     onClick={() => openExternalLink(link)}>
                                                    {link}
                                                </div>
                                            </div>
                                            <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                <button
                                                    onClick={() => openExternalLink(link)}
                                                    className="p-2 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded-lg transition-colors"
                                                    title="Open document link"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => startEditing(`doc_${index}`, link)}
                                                    className="p-2 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded-lg transition-colors"
                                                    title="Edit document link"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const newLinks = buildSummaryData.documentLinks.filter((_, i) => i !== index);
                                                        setBuildSummaryData(prev => prev ? {...prev, documentLinks: newLinks} : null);
                                                    }}
                                                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                                                    title="Remove document link"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Monitoring */}
                <div className="border-t border-white/10 pt-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-semibold text-white flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mr-3 shadow-lg">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            System Monitoring & Analytics
                        </h3>
                    </div>
                    <div className="space-y-4">
                        <div className="bg-gradient-to-r from-white/5 to-white/10 border border-white/20 rounded-xl p-6 group">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-medium text-white flex items-center">
                                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center mr-3">
                                        <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    Key Performance Indicators
                                </h4>
                                <button
                                    onClick={() => startEditing('keyMetrics', buildSummaryData.keyMetrics)}
                                    className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                    title="Edit key metrics"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </button>
                            </div>
                            
                            {editingField === 'keyMetrics' ? (
                                <div className="space-y-3">
                                    <textarea
                                        value={editValue}
                                        onChange={(e) => setBuildSummaryData(prev => prev ? {...prev, keyMetrics: e.target.value} : null)}
                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400/50 transition-all duration-300 backdrop-blur-sm min-h-[100px] resize-none"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && e.ctrlKey) saveEdit();
                                            if (e.key === 'Escape') cancelEdit();
                                        }}
                                        autoFocus
                                        placeholder="Describe your key performance indicators..."
                                    />
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={saveEdit}
                                            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center space-x-1"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span>Save (Ctrl+Enter)</span>
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center space-x-1"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            <span>Cancel (Esc)</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-white/90 text-sm leading-relaxed whitespace-pre-wrap">
                                    {buildSummaryData.keyMetrics}
                                </div>
                            )}
                        </div>
                        
                        <div className="bg-gradient-to-r from-white/5 to-white/10 border border-white/20 rounded-xl p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-lg font-medium text-white flex items-center">
                                    <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center mr-3">
                                        <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    Monitoring Dashboards
                                </h4>
                                <button
                                    onClick={() => {
                                        const newLinks = [...buildSummaryData.dashboardLinks, ''];
                                        setBuildSummaryData(prev => prev ? {...prev, dashboardLinks: newLinks} : null);
                                    }}
                                    className="px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors flex items-center space-x-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    <span>Add Dashboard</span>
                                </button>
                            </div>
                            
                            <div className="space-y-3">
                                {buildSummaryData.dashboardLinks.map((link, index) => (
                                    <div key={index} className="group">
                                        {editingField === `dashboard_${index}` ? (
                                            <div className="flex items-center space-x-3">
                                                <div className="flex-1">
                                                    <input
                                                        type="text"
                                                        value={editValue}
                                                        onChange={(e) => {
                                                            const newLinks = [...buildSummaryData.dashboardLinks];
                                                            newLinks[index] = e.target.value;
                                                            setBuildSummaryData(prev => prev ? {...prev, dashboardLinks: newLinks} : null);
                                                        }}
                                                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 transition-all duration-300 backdrop-blur-sm"
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveEdit();
                                                            if (e.key === 'Escape') cancelEdit();
                                                        }}
                                                        autoFocus
                                                        placeholder="Enter dashboard URL..."
                                                    />
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={saveEdit}
                                                        className="px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center space-x-1"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        <span>Save</span>
                                                    </button>
                                                    <button
                                                        onClick={cancelEdit}
                                                        className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors flex items-center space-x-1"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                        <span>Cancel</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="bg-white/5 border border-white/20 rounded-lg p-3 hover:bg-white/10 transition-all duration-300 group-hover:shadow-md">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-6 h-6 bg-purple-500/20 rounded-md flex items-center justify-center">
                                                            <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                            </svg>
                                                        </div>
                                                        <div className="text-white text-sm font-medium break-all cursor-pointer hover:text-purple-400 transition-colors"
                                                             onClick={() => openExternalLink(link)}>
                                                            {link}
                                                        </div>
                                                    </div>
                                                    <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                        <button
                                                            onClick={() => openExternalLink(link)}
                                                            className="p-1.5 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 rounded transition-colors"
                                                            title="Open dashboard link"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => startEditing(`dashboard_${index}`, link)}
                                                            className="p-1.5 text-purple-400 hover:text-purple-300 hover:bg-purple-500/20 rounded transition-colors"
                                                            title="Edit dashboard link"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                const newLinks = buildSummaryData.dashboardLinks.filter((_, i) => i !== index);
                                                                setBuildSummaryData(prev => prev ? {...prev, dashboardLinks: newLinks} : null);
                                                            }}
                                                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                                                            title="Remove dashboard link"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-white/90 mb-2">
                                    Alerts
                                </label>
                                <div className="relative group">
                                    {editingField === 'alerts' ? (
                                        <div className="space-y-2">
                                            <textarea
                                                value={editValue}
                                                onChange={(e) => setBuildSummaryData(prev => prev ? {...prev, alerts: e.target.value} : null)}
                                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 transition-all duration-300 backdrop-blur-sm min-h-[80px] resize-none"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.ctrlKey) saveEdit();
                                                    if (e.key === 'Escape') cancelEdit();
                                                }}
                                                autoFocus
                                            />
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={saveEdit}
                                                    className="px-3 py-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded transition-colors text-sm"
                                                >
                                                    Save (Ctrl+Enter)
                                                </button>
                                                <button
                                                    onClick={cancelEdit}
                                                    className="px-3 py-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors text-sm"
                                                >
                                                    Cancel (Esc)
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white min-h-[80px] whitespace-pre-wrap text-sm">
                                                {buildSummaryData.alerts}
                                            </div>
                                            <button
                                                onClick={() => startEditing('alerts', buildSummaryData.alerts)}
                                                className="absolute right-2 top-2 p-1.5 text-white/40 hover:text-white/90 hover:bg-white/20 rounded transition-all duration-200 opacity-0 group-hover:opacity-100"
                                                title="Edit alerts"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-white/90 mb-2">
                                    Logs
                                </label>
                                <div className="relative group">
                                    {editingField === 'logs' ? (
                                        <div className="space-y-2">
                                            <textarea
                                                value={editValue}
                                                onChange={(e) => setBuildSummaryData(prev => prev ? {...prev, logs: e.target.value} : null)}
                                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 transition-all duration-300 backdrop-blur-sm min-h-[80px] resize-none"
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && e.ctrlKey) saveEdit();
                                                    if (e.key === 'Escape') cancelEdit();
                                                }}
                                                autoFocus
                                            />
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={saveEdit}
                                                    className="px-3 py-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded transition-colors text-sm"
                                                >
                                                    Save (Ctrl+Enter)
                                                </button>
                                                <button
                                                    onClick={cancelEdit}
                                                    className="px-3 py-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors text-sm"
                                                >
                                                    Cancel (Esc)
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white min-h-[80px] whitespace-pre-wrap text-sm">
                                                {buildSummaryData.logs}
                                            </div>
                                            <button
                                                onClick={() => startEditing('logs', buildSummaryData.logs)}
                                                className="absolute right-2 top-2 p-1.5 text-white/40 hover:text-white/90 hover:bg-white/20 rounded transition-all duration-200 opacity-0 group-hover:opacity-100"
                                                title="Edit logs"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Performance Metrics Charts */}
                        <div className="mt-6">
                            <h4 className="text-md font-medium text-white/90 mb-4">Performance Metrics (Last 7 Days)</h4>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Response Time Chart */}
                                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <h5 className="text-sm font-medium text-white/80 mb-3">Response Time (ms)</h5>
                                    <div className="h-32 relative">
                                        <svg className="w-full h-full" viewBox="0 0 300 120" preserveAspectRatio="none">
                                            {/* Grid lines */}
                                            <defs>
                                                <pattern id="grid" width="50" height="20" patternUnits="userSpaceOnUse">
                                                    <path d="M 50 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
                                                </pattern>
                                            </defs>
                                            <rect width="100%" height="100%" fill="url(#grid)" />
                                            
                                            {/* Y-axis labels */}
                                            <text x="5" y="15" fill="rgba(255,255,255,0.6)" fontSize="12">200</text>
                                            <text x="5" y="35" fill="rgba(255,255,255,0.6)" fontSize="12">150</text>
                                            <text x="5" y="55" fill="rgba(255,255,255,0.6)" fontSize="12">100</text>
                                            <text x="5" y="75" fill="rgba(255,255,255,0.6)" fontSize="12">50</text>
                                            <text x="5" y="95" fill="rgba(255,255,255,0.6)" fontSize="12">0</text>
                                            
                                            {/* X-axis labels */}
                                            <text x="50" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Mon</text>
                                            <text x="100" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Tue</text>
                                            <text x="150" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Wed</text>
                                            <text x="200" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Thu</text>
                                            <text x="250" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Fri</text>
                                            <text x="300" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Sat</text>
                                            
                                            {/* Response Time Line */}
                                            <path
                                                d="M 0 60 L 50 45 L 100 35 L 150 55 L 200 25 L 250 40 L 300 30"
                                                fill="none"
                                                stroke="#60A5FA"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            
                                            {/* Data points */}
                                            <circle cx="0" cy="60" r="3" fill="#60A5FA" />
                                            <circle cx="50" cy="45" r="3" fill="#60A5FA" />
                                            <circle cx="100" cy="35" r="3" fill="#60A5FA" />
                                            <circle cx="150" cy="55" r="3" fill="#60A5FA" />
                                            <circle cx="200" cy="25" r="3" fill="#60A5FA" />
                                            <circle cx="250" cy="40" r="3" fill="#60A5FA" />
                                            <circle cx="300" cy="30" r="3" fill="#60A5FA" />
                                        </svg>
                                    </div>
                                    <div className="mt-2 text-xs text-white/60">
                                        <span className="text-green-400">↓ 12%</span> from last week
                                    </div>
                                </div>
                                
                                {/* Error Rate Chart */}
                                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                                    <h5 className="text-sm font-medium text-white/80 mb-3">Error Rate (%)</h5>
                                    <div className="h-32 relative">
                                        <svg className="w-full h-full" viewBox="0 0 300 120" preserveAspectRatio="none">
                                            {/* Grid lines */}
                                            <defs>
                                                <pattern id="grid2" width="50" height="20" patternUnits="userSpaceOnUse">
                                                    <path d="M 50 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
                                                </pattern>
                                            </defs>
                                            <rect width="100%" height="100%" fill="url(#grid2)" />
                                            
                                            {/* Y-axis labels */}
                                            <text x="5" y="15" fill="rgba(255,255,255,0.6)" fontSize="12">5.0</text>
                                            <text x="5" y="35" fill="rgba(255,255,255,0.6)" fontSize="12">4.0</text>
                                            <text x="5" y="55" fill="rgba(255,255,255,0.6)" fontSize="12">3.0</text>
                                            <text x="5" y="75" fill="rgba(255,255,255,0.6)" fontSize="12">2.0</text>
                                            <text x="5" y="95" fill="rgba(255,255,255,0.6)" fontSize="12">1.0</text>
                                            
                                            {/* X-axis labels */}
                                            <text x="50" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Mon</text>
                                            <text x="100" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Tue</text>
                                            <text x="150" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Wed</text>
                                            <text x="200" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Thu</text>
                                            <text x="250" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Fri</text>
                                            <text x="300" y="115" fill="rgba(255,255,255,0.6)" fontSize="12">Sat</text>
                                            
                                            {/* Error Rate Line */}
                                            <path
                                                d="M 0 80 L 50 70 L 100 85 L 150 65 L 200 90 L 250 75 L 300 60"
                                                fill="none"
                                                stroke="#F87171"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            
                                            {/* Data points */}
                                            <circle cx="0" cy="80" r="3" fill="#F87171" />
                                            <circle cx="50" cy="70" r="3" fill="#F87171" />
                                            <circle cx="100" cy="85" r="3" fill="#F87171" />
                                            <circle cx="150" cy="65" r="3" fill="#F87171" />
                                            <circle cx="200" cy="90" r="3" fill="#F87171" />
                                            <circle cx="250" cy="75" r="3" fill="#F87171" />
                                            <circle cx="300" cy="60" r="3" fill="#F87171" />
                                        </svg>
                                    </div>
                                    <div className="mt-2 text-xs text-white/60">
                                        <span className="text-green-400">↓ 8%</span> from last week
                                    </div>
                                </div>
                            </div>
                            
                            {/* Additional Metrics Summary */}
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                                    <div className="text-lg font-semibold text-green-400">99.2%</div>
                                    <div className="text-xs text-white/60">Uptime</div>
                                </div>
                                <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                                    <div className="text-lg font-semibold text-blue-400">245ms</div>
                                    <div className="text-xs text-white/60">Avg Response</div>
                                </div>
                                <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                                    <div className="text-lg font-semibold text-yellow-400">2.1%</div>
                                    <div className="text-xs text-white/60">Error Rate</div>
                                </div>
                                <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                                    <div className="text-lg font-semibold text-purple-400">1.2K</div>
                                    <div className="text-xs text-white/60">Requests/min</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Owners */}
                <div className="border-t border-white/10 pt-4">
                    <h3 className="text-lg font-medium text-white flex items-center mb-3">
                        <svg className="w-5 h-5 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                        </svg>
                        Owners
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-white/90 mb-2">
                                Product Manager
                            </label>
                            <div className="relative group">
                                {editingField === 'productManager' ? (
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setBuildSummaryData(prev => prev ? {...prev, productManager: e.target.value} : null)}
                                            className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 transition-all duration-300 backdrop-blur-sm"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveEdit();
                                                if (e.key === 'Escape') cancelEdit();
                                            }}
                                            autoFocus
                                        />
                                        <button
                                            onClick={saveEdit}
                                            className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded transition-colors"
                                            title="Save"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                                            title="Cancel"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm">
                                            {buildSummaryData.productManager}
                                        </div>
                                        <button
                                            onClick={() => startEditing('productManager', buildSummaryData.productManager)}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-white/40 hover:text-white/90 hover:bg-white/20 rounded transition-all duration-200 opacity-0 group-hover:opacity-100"
                                            title="Edit product manager"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-white/90 mb-2">
                                Dev Manager
                            </label>
                            <div className="relative group">
                                {editingField === 'devManager' ? (
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setBuildSummaryData(prev => prev ? {...prev, devManager: e.target.value} : null)}
                                            className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 transition-all duration-300 backdrop-blur-sm"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveEdit();
                                                if (e.key === 'Escape') cancelEdit();
                                            }}
                                            autoFocus
                                        />
                                        <button
                                            onClick={saveEdit}
                                            className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded transition-colors"
                                            title="Save"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={cancelEdit}
                                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                                            title="Cancel"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm">
                                            {buildSummaryData.devManager}
                                        </div>
                                        <button
                                            onClick={() => startEditing('devManager', buildSummaryData.devManager)}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-white/40 hover:text-white/90 hover:bg-white/20 rounded transition-all duration-200 opacity-0 group-hover:opacity-100"
                                            title="Edit dev manager"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="border-t border-white/10 pt-4 flex justify-end space-x-4">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 text-sm font-medium text-white/70 hover:text-white border border-white/20 rounded-lg hover:bg-white/5 transition-all duration-300"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            // Here you can add logic to save the build summary data
                            console.log('Build Summary Data:', buildSummaryData);
                            onClose();
                        }}
                        className="px-6 py-2 text-sm font-semibold bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white shadow-lg hover:shadow-purple-500/25 rounded-lg transition-all duration-300"
                    >
                        Save Changes
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default BuildSummaryModal;
